import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Camera,
  Phone,
  Mail,
  User,
  Monitor,
  LogOut,
  Cake,
  Info,
  Check,
  Plus,
  Users,
  ArrowLeft,
  Pencil,
  Trash2,
  IdCard,
  KeyRound,
  Wallet,
  Gift,
  ChevronDown,
  HelpCircle,
  Download,
  X,
  Loader2,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useMyProfile } from "../hooks/useProfile";
import BadgeStrip from "../components/community/badges/BadgeStrip";
import { getCreditsHistory } from "../services/api";
import TripSecureBenefitsDialog from "../components/account/TripSecureBenefitsDialog";
import * as Sentry from "@sentry/react";
import { PaymentError } from "../errors/sentryErrors";
import RedeemRewards from "../components/account/RedeemRewards";
import { FUNNEL_URL } from "../config";

/**
 * Account settings — wired to the Funnel's /api/account/* endpoints (see
 * PROFILE_SECTION_FRONTEND.md). Both apps share one login (the session
 * cookie), so every call below carries `credentials: "include"` rather than
 * anything read from localStorage.
 *
 * Wallet stays exactly as it was: that data is real and already lives on
 * Romaya's own backend (getCreditsHistory / useMyProfile), untouched here.
 */

// Same env var AuthContext already uses for the Funnel base address — reused
// rather than re-declared, so there is exactly one place this can drift.

class ApiError extends Error {
  constructor(code, opts = {}) {
    super(code);
    this.code = code;
    this.issues = opts.issues;
    this.limit = opts.limit;
    this.retryAfter = opts.retryAfter;
  }
}

async function callFunnelApi(path, init = {}) {
  const res = await fetch(`${FUNNEL_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });
  if (res.status === 204) return null;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 429) {
      const retryAfter = Number(res.headers.get("Retry-After") || "0") || undefined;
      throw new ApiError("rate_limited", { retryAfter });
    }
    throw new ApiError(body.error || `http_${res.status}`, { issues: body.issues, limit: body.limit });
  }
  return body;
}

function friendlyMessage(err) {
  if (!(err instanceof ApiError)) return err?.message || "Something went wrong.";
  switch (err.code) {
    case "unauthenticated":
      return "Your session has expired. Please sign in again.";
    case "validation_failed":
      return err.issues?.[0]?.message || "Please check the highlighted fields.";
    case "phone_already_in_use":
      return "This number is already registered.";
    case "list_full":
      return `You can save up to ${err.limit} entries.`;
    case "file_too_large":
      return "Please choose a photo under 10 MB.";
    case "unsupported_file_type":
      return "Please choose a JPEG, PNG, WebP or GIF.";
    case "rate_limited":
      return err.retryAfter
        ? `Too many requests — try again in ${err.retryAfter}s.`
        : "Too many requests — please wait a moment.";
    case "not_found":
      return "That entry isn't there anymore.";
    default:
      return "Something went wrong. Please try again.";
  }
}

function issuesToFieldErrors(issues) {
  const out = {};
  for (const issue of issues || []) {
    const key = issue.path.join(".");
    if (key && !out[key]) out[key] = issue.message;
  }
  return out;
}

const toDateInput = (v) => (v ? String(v).slice(0, 10) : "");

const NAV_ITEMS = [
  { key: "profile", label: "My Profile", Icon: User },
  { key: "cotravellers", label: "Co-Travellers", Icon: Users },
  { key: "wallet", label: "Wallet", Icon: Wallet },
  // Hidden until session management exists. The panel below still renders for
  // key "devices", so restoring this line brings the whole section back.
  // { key: "devices", label: "Logged In Devices", Icon: Monitor },
];

// reason -> what shows in the transaction list. Falls back to a title-cased
// version of the raw code for any rule this map hasn't caught up with yet, so a
// newly added credit rule never renders as a blank row.
const CREDIT_REASON_LABELS = {
  REVIEW_SUBMITTED: "Trip review submitted",
  REVIEW_WITH_PHOTOS: "Review included photos",
  REVIEW_DETAILED_BONUS: "Super Reviewer bonus",
  POST_PUBLISHED: "Trip post published",
  POST_COPIED_FIRST: "Your itinerary was copied",
  PHOTO_UPLOADED: "Photo uploaded",
  VIDEO_UPLOADED: "Video uploaded",
  LIKES_MILESTONE_10: "10 likes on your post",
  SAVES_MILESTONE_10: "10 saves on your post",
  COPIES_MILESTONE_10: "10 copies of your itinerary",
  FUNNEL_REVIEW_CONTRIBUTION: "RoamMyWay trip review",
  // Inherited from the retired /credits page — this is now the only ledger
  // view, so it carries the full label set rather than the shorter one.
  ITINERARY_FIRST_COPY_REWARD: "First copy reward",
  COPIED_TRIP_REVIEWED: "Your copied itinerary was reviewed",
  FIRST_COPY_BONUS: "First itinerary you copied",
  SUBRATING_SUBMITTED: "Hotel/food/activity rating",
  MANUAL_ADJUSTMENT: "Admin adjustment",
};

// Every answer here is grounded in the actual credits system (creditsService.js,
// events/index.js) — no invented tiers, expiry windows, or partner-card terms.
const WALLET_FAQS = [
  {
    q: "What are RoamPoints?",
    a: "RoamPoints are our reward currency — you earn them by contributing to the travel community, and you can redeem them for a travel coupon. The same RoamPoints balance is used across Romaya and RoamMyWay.",
  },
  {
    q: "How do I earn RoamPoints?",
    a: "You earn RoamPoints for things like submitting a trip review, publishing a trip post with photos or video, having your itinerary copied by another traveller, and hitting milestones such as 10 likes, saves, or copies on a post.",
  },
  {
    q: "Do RoamPoints expire?",
    a: "Your RoamPoints balance itself doesn't expire. If you redeem RoamPoints for a specific reward (like a discount coupon), that reward may carry its own validity window — check the terms shown when you redeem it.",
  },
  {
    q: "When do I get RoamPoints for photos and videos?",
    a: "Once a moderator approves the upload — not the moment you upload it. Rejected media never earns RoamPoints.",
  },
  {
    q: "Will I lose RoamPoints if I delete my review, post, or photos?",
    a: "Yes. If you remove content that already earned you RoamPoints — for example deleting the last photo tied to a review bonus — the associated RoamPoints are reversed. This keeps the reward tied to content that's actually still live.",
  },
  {
    q: "What's the Super Reviewer bonus?",
    a: "An extra bonus for a genuinely detailed review — one with a description of 200+ characters plus your hotel, itinerary-ease, itinerary-satisfaction, and value-for-money ratings all filled in.",
  },
  {
    q: "Where can I use my RoamPoints?",
    a: "Redeem them on the My RoamPoints page for a travel coupon you can use on a RoamMyWay trip booking.",
  },
  {
    q: "How do I redeem my RoamPoints?",
    a: "Go to My RoamPoints, pick a reward you have enough balance for, and confirm the redemption. The RoamPoints are deducted immediately and your reward is issued.",
  },
];

// Single accordion row — expand state is local to the FAQ list, not persisted.
const FaqAccordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        return (
          <div key={item.q}>
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : i)}
              aria-expanded={isOpen}
              className="w-full flex items-center justify-between gap-4 px-4 py-3.5 text-left hover:bg-gray-50/60 transition-colors cursor-pointer"
            >
              <span className="text-sm font-medium text-gray-800">{item.q}</span>
              <ChevronDown
                size={16}
                className={`shrink-0 text-teal-600 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isOpen && (
              <div className="px-4 pb-4 -mt-1">
                <p className="text-sm text-gray-500 leading-relaxed">{item.a}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const creditReasonLabel = (reason) => {
  if (CREDIT_REASON_LABELS[reason]) return CREDIT_REASON_LABELS[reason];
  // Redemptions are spent here now, so their rows land in this same list.
  if (String(reason || "").startsWith("REDEEM_")) {
    return `Redeemed: ${reason.slice("REDEEM_".length).replace(/_/g, " ").toLowerCase()}`;
  }
  const stripped = String(reason || "").replace(/_REVOKED$/, "");
  const base = CREDIT_REASON_LABELS[stripped] || stripped.replace(/_/g, " ").toLowerCase();
  const titled = base.charAt(0).toUpperCase() + base.slice(1);
  return reason?.endsWith("_REVOKED") ? `${titled} (reversed)` : titled;
};

// Relationship chips, in the order the design lists them.
const RELATIONSHIPS = [
  "Spouse", "Child", "Sibling", "GrandParent", "Friend",
  "Parent", "Colleague", "Relative", "Parent in law", "Other",
];

const MEAL_PREFERENCES = ["Veg", "Non-veg", "Jain", "Vegan", "No preference"];
const BERTH_PREFERENCES = ["Lower", "Middle", "Upper", "Side lower", "Side upper", "No preference"];

const EMPTY_TRAVELLER = {
  firstName: "",
  lastName: "",
  gender: "",
  dob: "",
  nationality: "",
  relationship: "",
  meal: "",
  berth: "",
  passportNo: "",
  passportExpiry: "",
  issuingCountry: "",
  phone: "",
  email: "",
  airline: "",
  frequentFlyerNumber: "",
};

// "Male, 21y, 4 Aug 2005" — age derived from the DOB rather than stored, so it
// cannot drift out of date.
const describeTraveller = (t) => {
  const bits = [];
  if (t.gender) bits.push(t.gender.charAt(0).toUpperCase() + t.gender.slice(1));

  if (t.dob) {
    const born = new Date(t.dob);
    if (!Number.isNaN(born.getTime())) {
      const now = new Date();
      let age = now.getFullYear() - born.getFullYear();
      const beforeBirthday =
        now.getMonth() < born.getMonth() ||
        (now.getMonth() === born.getMonth() && now.getDate() < born.getDate());
      if (beforeBirthday) age -= 1;
      if (age >= 0) bits.push(`${age}y`);
      bits.push(born.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }));
    }
  }
  return bits.join(", ");
};

const INSURANCE_ROWS = [
  { key: "domesticHotel", context: "On booking a domestic hotel", label: "Add Trip Secure (Hotels)" },
  { key: "domesticFlight", context: "On booking a domestic flight", label: "Add Trip Secure (Flights)" },
  {
    key: "intlHotel",
    context: "On booking an international hotel",
    label: "Add International Travel & Medical Insurance (Hotels)",
  },
  {
    key: "intlFlight",
    context: "On booking an international flight",
    label: "Add International Travel & Medical Insurance (Flights)",
  },
];

const AIRLINES = [
  "IndiGo",
  "Air India",
  "Vistara",
  "SpiceJet",
  "Akasa Air",
  "AirAsia India",
  "Emirates",
  "Qatar Airways",
  "Etihad Airways",
  "Singapore Airlines",
  "British Airways",
  "Lufthansa",
  "Other",
];

const Field = ({ label, children, error }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 mb-1.5">
      {label}
    </span>
    {children}
    {error && <span className="block text-xs text-red-600 mt-1">{error}</span>}
  </label>
);

const inputClass =
  "w-full rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10";
const inputErrorClass = "border-red-400 focus:border-red-500 focus:ring-red-500/10";

const Section = ({ title, description, children }) => (
  <section className="pt-7 mt-7 border-t border-gray-100 first:pt-0 first:mt-0 first:border-0">
    <h2 className="text-lg font-semibold tracking-[-0.01em] text-gray-900">{title}</h2>
    {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
    <div className="mt-5">{children}</div>
  </section>
);

// A small self-contained coin that flips in place, reusing the same gold/amber
// coin drawn for the reward-flight animation elsewhere (WriteReview.jsx) so the
// "credits" motif stays visually consistent across the app. Purely decorative —
// `coin-flip` respects prefers-reduced-motion (see index.css).
const AnimatedWalletCoin = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" className="coin-flip" aria-hidden="true">
    <circle cx="10" cy="10" r="9" fill="#fde047" stroke="#f59e0b" strokeWidth="2" />
    <circle cx="10" cy="10" r="4.5" fill="none" stroke="#f59e0b" strokeWidth="1.5" />
  </svg>
);

export default function AccountProfile() {
  const { user, logout, loading: authLoading, isAuthenticated } = useAuth();
  const { data: myProfile, refetch: refetchProfile } = useMyProfile();
  const navigate = useNavigate();

  // Lets other pages deep-link to a specific tab, e.g. /account?tab=wallet
  // from the dashboard's "View wallet" button, instead of only landing on
  // My Profile and making the traveller click Wallet themselves.
  const [searchParams] = useSearchParams();
  const VALID_NAV_TABS = ["profile", "cotravellers", "wallet", "devices", "faqs"];
  const requestedTab = searchParams.get("tab");
  const [activeNav, setActiveNav] = useState(
    VALID_NAV_TABS.includes(requestedTab) ? requestedTab : "profile",
  );
  // ---- Profile load + form state (wired to Funnel's /api/account/profile) ----
  const [profileLoading, setProfileLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    firstName: (user?.name || "").split(" ").slice(0, -1).join(" ") || user?.name || "",
    lastName: (user?.name || "").split(" ").slice(-1).join(" ") || "",
    phone: "",
    gender: "",
    dob: "",
    nationality: "",
    passportNo: "",
    passportExpiry: "",
    issuingCountry: "",
    panNumber: "",
  });
  const [insurance, setInsurance] = useState({});
  const [benefitsOpen, setBenefitsOpen] = useState(false);

  const applyProfile = (p) => {
    setProfile(p);
    setAvatarUrl(p.image || null);
    const [firstName, ...rest] = (p.name || "").split(" ");
    setForm({
      firstName: firstName || "",
      lastName: rest.join(" "),
      phone: p.phone || "",
      gender: p.gender || "",
      dob: toDateInput(p.dob),
      nationality: p.nationality || "",
      passportNo: p.passportNo || "",
      passportExpiry: toDateInput(p.passportExpiry),
      issuingCountry: p.issuingCountry || "",
      panNumber: p.panNumber || "",
    });
    setInsurance(p.insurancePreferences || {});
  };

  const [frequentFlyers, setFrequentFlyers] = useState([]);

  const fromApiTraveller = (c) => ({
    id: c.id,
    firstName: c.firstName || "",
    lastName: c.lastName || "",
    gender: c.gender || "",
    dob: toDateInput(c.dob),
    nationality: c.nationality || "",
    relationship: c.relationship || "",
    meal: c.meal || "",
    berth: c.berth || "",
    passportNo: c.passportNo || "",
    passportExpiry: toDateInput(c.passportExpiry),
    issuingCountry: c.issuingCountry || "",
    phone: c.phone || "",
    email: c.email || "",
    airline: c.airline || "",
    frequentFlyerNumber: c.frequentFlyerNumber || "",
  });

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await callFunnelApi("/api/account/profile");
        if (cancelled) return;
        applyProfile(data.profile);
        setFrequentFlyers(data.frequentFlyers || []);
        setCoTravellers((data.coTravelers || []).map(fromApiTraveller));
      } catch (err) {
        toast.error(friendlyMessage(err));
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated]);

  // ---- Avatar ----
  const uploadAvatar = async (file) => {
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${FUNNEL_URL}/api/account/avatar`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 413) throw new ApiError("file_too_large");
        if (res.status === 415) throw new ApiError("unsupported_file_type");
        throw new ApiError(data.error || `http_${res.status}`);
      }
      setAvatarUrl(data.url);
      toast.success("Photo updated.");
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const removeAvatar = async () => {
    try {
      await callFunnelApi("/api/account/avatar", { method: "DELETE" });
      setAvatarUrl(null);
      toast.success("Photo removed.");
    } catch (err) {
      toast.error(friendlyMessage(err));
    }
  };

  // ---- Frequent flyers ----
  const addFrequentFlyer = () => {
    setFrequentFlyers((prev) => [...prev, { id: `local-${Date.now()}-${prev.length}`, airline: "", number: "" }]);
  };
  const updateFrequentFlyer = (id, field, value) => {
    setFrequentFlyers((prev) => prev.map((f) => (f.id === id ? { ...f, [field]: value } : f)));
  };
  const commitFrequentFlyer = async (flyer) => {
    if (!flyer.airline.trim() || !flyer.number.trim()) return;
    try {
      if (String(flyer.id).startsWith("local-")) {
        const data = await callFunnelApi("/api/account/frequent-flyers", {
          method: "POST",
          body: JSON.stringify({ airline: flyer.airline, number: flyer.number }),
        });
        setFrequentFlyers((prev) => prev.map((f) => (f.id === flyer.id ? data.frequentFlyer : f)));
      } else {
        const data = await callFunnelApi(`/api/account/frequent-flyers/${flyer.id}`, {
          method: "PATCH",
          body: JSON.stringify({ airline: flyer.airline, number: flyer.number }),
        });
        setFrequentFlyers((prev) => prev.map((f) => (f.id === flyer.id ? data.frequentFlyer : f)));
      }
    } catch (err) {
      toast.error(friendlyMessage(err));
    }
  };
  const removeFrequentFlyer = async (id) => {
    if (String(id).startsWith("local-")) {
      setFrequentFlyers((prev) => prev.filter((f) => f.id !== id));
      return;
    }
    const prev = frequentFlyers;
    setFrequentFlyers((cur) => cur.filter((f) => f.id !== id));
    try {
      await callFunnelApi(`/api/account/frequent-flyers/${id}`, { method: "DELETE" });
    } catch (err) {
      setFrequentFlyers(prev);
      toast.error(friendlyMessage(err));
    }
  };

  // Recent wallet activity, fetched only once the Wallet tab is actually opened
  // rather than on every account-page load. The API is cursor-paginated (there's
  // no page-number concept server-side), so each fetched batch is cached here —
  // `walletPages[i].nextCursor` is what "Next" fetches with, and going "Previous"
  // just re-shows an already-cached page instead of re-fetching it.
  const WALLET_PAGE_SIZE = 10;
  const [walletPages, setWalletPages] = useState([]); // [{ data, nextCursor }]
  const [walletPageIndex, setWalletPageIndex] = useState(0);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletPaginating, setWalletPaginating] = useState(false);
  const [walletError, setWalletError] = useState(null);
  // Statement date filter — applied client-side over the currently loaded page,
  // so it stays instant with no extra request. Empty means "no bound".
  const [walletFrom, setWalletFrom] = useState("");
  const [walletTo, setWalletTo] = useState("");
  // Click-toggle for the balance tooltip, so it also works on touch devices
  // that have no hover state — desktop still gets it on hover via CSS.
  const [balanceInfoOpen, setBalanceInfoOpen] = useState(false);

  useEffect(() => {
    if (activeNav !== "wallet" || walletPages.length > 0) return;
    let cancelled = false;
    setWalletLoading(true);
    setWalletError(null);
    getCreditsHistory(null, WALLET_PAGE_SIZE)
      .then((res) => {
        if (!cancelled) {
          setWalletPages([{ data: res?.data || [], nextCursor: res?.meta?.nextCursor || null }]);
          setWalletPageIndex(0);
        }
      })
      .catch((err) => {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "payment");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new PaymentError(err instanceof Error ? err.message : String(err)));
        });
        if (!cancelled) setWalletError(err?.message || "Couldn't load your recent activity.");
      })
      .finally(() => {
        if (!cancelled) setWalletLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeNav, walletPages.length]);

  const walletCurrentPage = walletPages[walletPageIndex] || null;
  const walletHasPrev = walletPageIndex > 0;
  const walletHasNext = walletPageIndex + 1 < walletPages.length || !!walletCurrentPage?.nextCursor;

  // Same From/To bounds the table applies — the download always matches what's
  // on screen, not the full unfiltered history.
  const walletFilteredRows = (() => {
    if (!walletCurrentPage?.data?.length) return [];
    const fromTime = walletFrom ? new Date(`${walletFrom}T00:00:00`).getTime() : null;
    const toTime = walletTo ? new Date(`${walletTo}T23:59:59`).getTime() : null;
    return walletCurrentPage.data.filter((tx) => {
      const t = new Date(tx.createdAt).getTime();
      if (fromTime !== null && t < fromTime) return false;
      if (toTime !== null && t > toTime) return false;
      return true;
    });
  })();

  const escapeCsvCell = (value) => {
    const str = String(value ?? "");
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const handleDownloadStatement = () => {
    if (walletFilteredRows.length === 0) return;
    const header = ["Date", "Time", "Transaction ID", "Type", "Description", "Amount"];
    const rows = walletFilteredRows.map((tx) => {
      const created = new Date(tx.createdAt);
      const isCredit = tx.amount > 0;
      return [
        created.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" }),
        created.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
        tx.id,
        isCredit ? "Credit" : "Debit",
        creditReasonLabel(tx.reason),
        tx.amount,
      ];
    });
    const csv = [header, ...rows].map((row) => row.map(escapeCsvCell).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `romaya-wallet-statement-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleWalletPrev = () => {
    setWalletPageIndex((i) => Math.max(0, i - 1));
  };

  const handleWalletNext = async () => {
    if (walletPageIndex + 1 < walletPages.length) {
      setWalletPageIndex((i) => i + 1);
      return;
    }
    if (!walletCurrentPage?.nextCursor) return;
    setWalletPaginating(true);
    try {
      const res = await getCreditsHistory(walletCurrentPage.nextCursor, WALLET_PAGE_SIZE);
      setWalletPages((pages) => [...pages, { data: res?.data || [], nextCursor: res?.meta?.nextCursor || null }]);
      setWalletPageIndex((i) => i + 1);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "payment");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new PaymentError(err instanceof Error ? err.message : String(err)));
      });
      toast.error(err?.message || "Couldn't load more transactions.");
    } finally {
      setWalletPaginating(false);
    }
  };

  // Co-travellers, wired to /api/account/co-travelers.
  const [coTravellers, setCoTravellers] = useState([]);
  const [travellerDraft, setTravellerDraft] = useState(null); // null = list view
  const [travellerSaving, setTravellerSaving] = useState(false);
  const setDraft = (key) => (e) =>
    setTravellerDraft((prev) => ({ ...prev, [key]: e.target.value }));

  const travellerToPayload = (t) => ({
    firstName: t.firstName,
    lastName: t.lastName || null,
    gender: t.gender || null,
    dob: t.dob || null,
    nationality: t.nationality || null,
    relationship: t.relationship || null,
    meal: t.meal || null,
    berth: t.berth || null,
    passportNo: t.passportNo || null,
    passportExpiry: t.passportExpiry || null,
    issuingCountry: t.issuingCountry || null,
    phone: t.phone || null,
    email: t.email || null,
    airline: t.airline || null,
    frequentFlyerNumber: t.frequentFlyerNumber || null,
  });

  const saveTraveller = async () => {
    const draft = travellerDraft;
    if (!draft?.firstName?.trim()) {
      toast.error("Add at least a first name.");
      return;
    }
    setTravellerSaving(true);
    setFieldErrors({});
    try {
      if (draft.id) {
        const data = await callFunnelApi(`/api/account/co-travelers/${draft.id}`, {
          method: "PATCH",
          body: JSON.stringify(travellerToPayload(draft)),
        });
        const saved = fromApiTraveller(data.coTraveler);
        setCoTravellers((prev) => prev.map((t) => (t.id === saved.id ? saved : t)));
      } else {
        const data = await callFunnelApi("/api/account/co-travelers", {
          method: "POST",
          body: JSON.stringify(travellerToPayload(draft)),
        });
        setCoTravellers((prev) => [...prev, fromApiTraveller(data.coTraveler)]);
      }
      setTravellerDraft(null);
      toast.success("Co-traveller saved.");
    } catch (err) {
      if (err instanceof ApiError && err.code === "validation_failed") {
        setFieldErrors(issuesToFieldErrors(err.issues));
      }
      toast.error(friendlyMessage(err));
    } finally {
      setTravellerSaving(false);
    }
  };

  const removeTraveller = async (id) => {
    if (!id) return;
    const prev = coTravellers;
    setCoTravellers((cur) => cur.filter((t) => t.id !== id));
    try {
      await callFunnelApi(`/api/account/co-travelers/${id}`, { method: "DELETE" });
      toast.success("Co-traveller removed.");
    } catch (err) {
      setCoTravellers(prev);
      toast.error(friendlyMessage(err));
    }
  };

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setFieldErrors({});
    try {
      const savedName = profile?.name || "";
      const [savedFirst, ...savedRest] = savedName.split(" ");
      const savedLast = savedRest.join(" ");

      const body = {};
      if (form.firstName !== (savedFirst || "")) body.firstName = form.firstName;
      if (form.lastName !== savedLast) body.lastName = form.lastName || null;
      if (form.phone !== (profile?.phone || "")) body.phone = form.phone || null;
      if (form.gender !== (profile?.gender || "")) body.gender = form.gender || null;
      if (form.dob !== toDateInput(profile?.dob || null)) body.dob = form.dob || null;
      if (form.nationality !== (profile?.nationality || "")) body.nationality = form.nationality || null;
      if (form.passportNo !== (profile?.passportNo || "")) body.passportNo = form.passportNo || null;
      if (form.passportExpiry !== toDateInput(profile?.passportExpiry || null))
        body.passportExpiry = form.passportExpiry || null;
      if (form.issuingCountry !== (profile?.issuingCountry || "")) body.issuingCountry = form.issuingCountry || null;
      if (form.panNumber !== (profile?.panNumber || "")) body.panNumber = form.panNumber || null;

      const savedInsurance = profile?.insurancePreferences || {};
      if (JSON.stringify(insurance) !== JSON.stringify(savedInsurance)) {
        body.insurancePreferences = insurance;
      }

      if (Object.keys(body).length === 0) {
        toast.info("Nothing to save.");
        return;
      }

      const data = await callFunnelApi("/api/account/profile", {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      applyProfile(data.profile);
      toast.success("Profile saved.");
    } catch (err) {
      if (err instanceof ApiError && err.code === "validation_failed") {
        setFieldErrors(issuesToFieldErrors(err.issues));
      }
      toast.error(friendlyMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout?.();
    } finally {
      navigate("/");
    }
  };

  if (authLoading || (isAuthenticated && profileLoading)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Cover */}
      <div className="relative h-56 sm:h-64 bg-gray-800 pt-16 lg:pt-20">
        <img
          src="/account-cover.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />

        {/* Way back to the dashboard — this page otherwise has no exit besides
            the navbar or the browser's own back button. top-20/lg:top-24 clears
            the fixed site navbar (h-16 lg:h-20). */}
        <Link
          to="/dashboard"
          className="absolute left-4 sm:left-6 top-20 lg:top-24 z-10 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-black/30 backdrop-blur-md ring-1 ring-white/20 text-white text-[13px] font-medium hover:bg-black/50 transition-colors"
        >
          <ArrowLeft size={15} /> Dashboard
        </Link>

        <div className="relative h-full container mx-auto max-w-6xl px-4 sm:px-6 flex items-end pb-6">
          <div className="flex items-center gap-4 min-w-0">
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadAvatar(f);
              }}
            />
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={avatarUploading}
              className="relative w-20 h-20 shrink-0 rounded-full bg-white/15 backdrop-blur-md ring-2 ring-teal-400 flex flex-col items-center justify-center text-white hover:bg-white/25 transition-colors cursor-pointer overflow-hidden disabled:opacity-70"
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : avatarUploading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <>
                  <Camera size={18} />
                  <span className="text-[9px] font-semibold mt-0.5">Add Photo</span>
                </>
              )}
            </button>
            {avatarUrl && (
              <button
                type="button"
                onClick={removeAvatar}
                className="text-white/70 hover:text-white text-xs underline decoration-dotted cursor-pointer self-end mb-1"
              >
                Remove photo
              </button>
            )}

            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-[-0.02em] truncate">
                {user?.name || "Your profile"}
              </h1>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-1.5 text-white/80 text-sm">
                {user?.phone && (
                  <span className="flex items-center gap-1.5">
                    <Phone size={13} /> {user.phone}
                  </span>
                )}
                <span className="flex items-center gap-1.5 min-w-0">
                  <Mail size={13} className="shrink-0" />
                  <span className="truncate">{user?.email || "Add email address"}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 container mx-auto max-w-6xl px-4 sm:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <aside className="lg:w-64 shrink-0">
            <nav className="bg-white rounded-2xl border border-gray-200/70 shadow-sm p-2">
              <p className="px-3 pt-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                My account
              </p>
              {NAV_ITEMS.map((item) => {
                const isActive = activeNav === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setActiveNav(item.key)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      isActive
                        ? "bg-teal-700 text-white"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <item.Icon size={16} className={isActive ? "text-white" : "text-gray-400"} />
                    {item.label}
                  </button>
                );
              })}

              <div className="my-2 border-t border-gray-100" />
              <button
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <LogOut size={16} className="text-gray-400" /> Logout
              </button>

              {/* Reset password isn't wired up yet — hidden until there's a real flow behind it.
              <div className="my-2 border-t border-gray-100" />
              <button
                type="button"
                onClick={() => toast.info("Password reset isn't wired up yet.")}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <KeyRound size={16} className="text-gray-400" /> Reset password
              </button>
              */}

              <div className="my-2 border-t border-gray-100" />
              <button
                type="button"
                onClick={() => setActiveNav("faqs")}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  activeNav === "faqs"
                    ? "bg-teal-700 text-white"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <HelpCircle size={16} className={activeNav === "faqs" ? "text-white" : "text-gray-400"} /> FAQs
              </button>
            </nav>
          </aside>

          {/* Panel */}
          <div className="flex-1 min-w-0 bg-white rounded-2xl border border-gray-200/70 shadow-sm p-6 sm:p-8">
            {activeNav === "cotravellers" ? (
              travellerDraft ? (
                /* ---- Add / edit co-traveller ---- */
                <>
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3 min-w-0">
                      <button
                        type="button"
                        onClick={() => setTravellerDraft(null)}
                        aria-label="Back to co-travellers"
                        className="text-teal-700 hover:text-teal-800 cursor-pointer"
                      >
                        <ArrowLeft size={20} />
                      </button>
                      <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900 truncate">
                        {travellerDraft.id ? "Edit co-traveller" : "Add new co-traveller"}
                      </h2>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => setTravellerDraft(null)}
                        className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm font-semibold hover:bg-gray-200 transition-colors cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={saveTraveller}
                        disabled={travellerSaving}
                        className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800 transition-colors cursor-pointer disabled:opacity-60"
                      >
                        {travellerSaving && <Loader2 size={14} className="animate-spin" />}
                        Save
                      </button>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3.5 mb-7">
                    <IdCard size={18} className="text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Double-check that the first and last name, gender and date of birth match
                      their government ID, such as Aadhaar or passport.
                    </p>
                  </div>

                  <Section title="General information">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                      <Field label="First & middle name" error={fieldErrors.firstName}>
                        <input className={`${inputClass} ${fieldErrors.firstName ? inputErrorClass : ""}`} value={travellerDraft.firstName} onChange={setDraft("firstName")} />
                      </Field>
                      <Field label="Last name">
                        <input className={inputClass} value={travellerDraft.lastName} onChange={setDraft("lastName")} />
                      </Field>
                      <Field label="Gender">
                        <select className={inputClass} value={travellerDraft.gender} onChange={setDraft("gender")}>
                          <option value="">Select gender</option>
                          <option value="female">Female</option>
                          <option value="male">Male</option>
                          <option value="other">Other</option>
                        </select>
                      </Field>
                      <Field label="Date of birth">
                        <input type="date" className={inputClass} value={travellerDraft.dob} onChange={setDraft("dob")} />
                      </Field>
                      <div className="sm:col-span-2">
                        <Field label="Nationality">
                          <select className={inputClass} value={travellerDraft.nationality} onChange={setDraft("nationality")}>
                            <option value="">Select nationality</option>
                            <option value="IN">Indian</option>
                            <option value="other">Other</option>
                          </select>
                        </Field>
                      </div>
                    </div>

                    <div className="mt-5 rounded-xl border border-gray-200 p-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 mb-3">
                        Relationship with traveller
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {RELATIONSHIPS.map((rel) => {
                          const selected = travellerDraft.relationship === rel;
                          return (
                            <button
                              key={rel}
                              type="button"
                              onClick={() =>
                                setTravellerDraft((prev) => ({
                                  ...prev,
                                  relationship: selected ? "" : rel,
                                }))
                              }
                              className={`px-3.5 py-1.5 rounded-lg border text-xs font-medium transition-colors cursor-pointer ${
                                selected
                                  ? "bg-teal-50 border-teal-500 text-teal-700"
                                  : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                              }`}
                            >
                              {rel}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[11px] text-teal-700 mt-3">
                        This helps us personalise travel recommendations when you travel together.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4 mt-5">
                      <Field label="Meal preference">
                        <select className={inputClass} value={travellerDraft.meal} onChange={setDraft("meal")}>
                          <option value="">Select preference</option>
                          {MEAL_PREFERENCES.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </Field>
                      <Field label="Train berth preference">
                        <select className={inputClass} value={travellerDraft.berth} onChange={setDraft("berth")}>
                          <option value="">Select preference</option>
                          {BERTH_PREFERENCES.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                      </Field>
                    </div>
                  </Section>

                  <Section title="Passport details">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                      <Field label="Passport no.">
                        <input className={inputClass} value={travellerDraft.passportNo} onChange={setDraft("passportNo")} />
                      </Field>
                      <Field label="Expiry date">
                        <input type="date" className={inputClass} value={travellerDraft.passportExpiry} onChange={setDraft("passportExpiry")} />
                      </Field>
                      <Field label="Issuing country">
                        <select className={inputClass} value={travellerDraft.issuingCountry} onChange={setDraft("issuingCountry")}>
                          <option value="">Select country</option>
                          <option value="IN">India</option>
                          <option value="other">Other</option>
                        </select>
                      </Field>
                    </div>
                  </Section>

                  <Section
                    title="Contact details"
                    description="Add contact information to receive booking details and other alerts."
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                      <Field label="Mobile number" error={fieldErrors.phone}>
                        <input className={`${inputClass} ${fieldErrors.phone ? inputErrorClass : ""}`} placeholder="+91" value={travellerDraft.phone} onChange={setDraft("phone")} />
                      </Field>
                      <Field label="Email ID">
                        <input type="email" className={inputClass} placeholder="name@example.com" value={travellerDraft.email} onChange={setDraft("email")} />
                      </Field>
                    </div>
                  </Section>

                  <Section title="Frequent flyer details">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                      <Field label="Airline">
                        <input className={inputClass} placeholder="e.g. IndiGo" value={travellerDraft.airline} onChange={setDraft("airline")} />
                      </Field>
                      <Field label="Frequent flyer number">
                        <input className={inputClass} value={travellerDraft.frequentFlyerNumber} onChange={setDraft("frequentFlyerNumber")} />
                      </Field>
                    </div>
                  </Section>
                </>
              ) : (
                /* ---- Co-traveller list ---- */
                <>
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900">
                      Co-Travellers
                    </h2>
                    <button
                      type="button"
                      onClick={() => setTravellerDraft({ ...EMPTY_TRAVELLER })}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800 cursor-pointer"
                    >
                      <Plus size={15} /> Add new co-traveller
                    </button>
                  </div>

                  {coTravellers.length === 0 ? (
                    <div className="text-center py-16 border border-dashed border-gray-200 rounded-xl">
                      <Users size={30} className="mx-auto text-gray-300 mb-3" />
                      <h3 className="text-base font-semibold text-gray-900">No co-travellers yet</h3>
                      <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                        Save the people you travel with to fill their details in faster next time.
                      </p>
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {coTravellers.map((t) => (
                        <li
                          key={t.id}
                          className="flex items-center gap-4 rounded-xl border border-gray-200 px-4 py-3.5"
                        >
                          <span className="w-10 h-10 rounded-full bg-teal-600 text-white flex items-center justify-center font-semibold shrink-0">
                            {(t.firstName || "?").charAt(0).toUpperCase()}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-gray-900 truncate">
                              {[t.firstName, t.lastName].filter(Boolean).join(" ")}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {describeTraveller(t) || "No details added"}
                              {t.relationship ? ` · ${t.relationship}` : ""}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setTravellerDraft({ ...t })}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800 shrink-0 cursor-pointer"
                          >
                            <Pencil size={14} /> Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => removeTraveller(t.id)}
                            aria-label={`Remove ${t.firstName}`}
                            className="text-gray-300 hover:text-red-500 transition-colors shrink-0 cursor-pointer"
                          >
                            <Trash2 size={15} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )
            ) : activeNav === "wallet" ? (
              <>
                <div className="flex items-center justify-between gap-4 mb-6">
                  <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900">
                    Wallet
                  </h2>
                </div>

                {/* Balance. Real figure from /api/users/me/profile — same source
                    the dashboard's wallet card reads, so the two can never disagree. */}
                <div className="rounded-2xl bg-gradient-to-br from-teal-700 to-teal-800 text-white p-6 flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                    <AnimatedWalletCoin size={24} />
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-100">
                        Available balance
                      </p>
                      <span className="relative group">
                        <button
                          type="button"
                          onClick={() => setBalanceInfoOpen((v) => !v)}
                          onBlur={() => setBalanceInfoOpen(false)}
                          aria-label="What is a RoamPoint worth?"
                          className="flex text-teal-200 hover:text-white transition-colors cursor-pointer"
                        >
                          <Info size={13} />
                        </button>
                        <span
                          role="tooltip"
                          className={`absolute left-0 top-full mt-2 w-52 rounded-lg bg-gray-900 text-white text-xs leading-relaxed px-3 py-2 shadow-lg z-10 normal-case tracking-normal font-normal transition-opacity ${
                            balanceInfoOpen ? "opacity-100" : "opacity-0 pointer-events-none"
                          } group-hover:opacity-100 group-hover:pointer-events-auto`}
                        >
                          1 RoamPoint = ₹1. Redeem your balance for a travel coupon.
                          <span className="absolute -top-1 left-3 w-2 h-2 bg-gray-900 rotate-45" />
                        </span>
                      </span>
                    </div>
                    <p className="text-3xl font-bold tabular-nums leading-tight mt-0.5">
                      {myProfile?.stats?.credits !== undefined
                        ? myProfile.stats.credits.toLocaleString()
                        : "—"}
                      <span className="text-sm font-semibold text-teal-200 ml-1.5">RoamPoints</span>
                    </p>
                  </div>
                </div>

                <Section title="Wallet Statement">
                  {!walletLoading && !walletError && walletCurrentPage?.data?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        From
                        <input
                          type="date"
                          value={walletFrom}
                          onChange={(e) => setWalletFrom(e.target.value)}
                          max={walletTo || undefined}
                          className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 outline-none focus:border-teal-500"
                        />
                      </label>
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        To
                        <input
                          type="date"
                          value={walletTo}
                          onChange={(e) => setWalletTo(e.target.value)}
                          min={walletFrom || undefined}
                          className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 outline-none focus:border-teal-500"
                        />
                      </label>
                      {(walletFrom || walletTo) && (
                        <button
                          type="button"
                          onClick={() => { setWalletFrom(""); setWalletTo(""); }}
                          className="text-xs font-semibold text-teal-700 hover:text-teal-800 cursor-pointer"
                        >
                          Clear
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleDownloadStatement}
                        disabled={walletFilteredRows.length === 0}
                        className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 hover:text-teal-800 disabled:text-gray-300 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <Download size={13} /> Download statement (CSV)
                      </button>
                    </div>
                  )}

                  {walletLoading && (
                    <div className="flex items-center justify-center py-10">
                      <div className="w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}

                  {!walletLoading && walletError && (
                    <div className="text-center py-8">
                      <p className="text-sm text-red-500 mb-2">{walletError}</p>
                      <button
                        type="button"
                        onClick={() => { setWalletPages([]); setWalletPageIndex(0); }}
                        className="text-sm font-semibold text-teal-700 hover:text-teal-800 cursor-pointer"
                      >
                        Try again
                      </button>
                    </div>
                  )}

                  {!walletLoading && !walletError && walletCurrentPage?.data?.length === 0 && (
                    <div className="text-center py-10 border border-dashed border-gray-200 rounded-xl">
                      <Gift size={26} className="mx-auto text-gray-300 mb-2.5" />
                      <p className="text-sm text-gray-500">
                        No activity yet. Write a review or publish a trip to start earning.
                      </p>
                    </div>
                  )}

                  {!walletLoading && !walletError && walletCurrentPage?.data?.length > 0 && (() => {
                    const filtered = walletFilteredRows;

                    if (filtered.length === 0) {
                      return (
                        <div className="text-center py-10 border border-dashed border-gray-200 rounded-xl">
                          <p className="text-sm text-gray-500">No transactions in this date range.</p>
                        </div>
                      );
                    }

                    return (
                      <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-200 text-left">
                                <th className="px-4 py-2.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">Date</th>
                                <th className="px-4 py-2.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">Time</th>
                                <th className="px-4 py-2.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">Transaction ID</th>
                                <th className="px-4 py-2.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">Type</th>
                                <th className="px-4 py-2.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">Description</th>
                                <th className="px-4 py-2.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap text-right">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {filtered.map((tx) => {
                                const isCredit = tx.amount > 0;
                                const created = new Date(tx.createdAt);
                                return (
                                  <tr key={tx.id} className="hover:bg-gray-50/60">
                                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                      {created.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit", year: "numeric" })}
                                    </td>
                                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                                      {created.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                                    </td>
                                    <td className="px-4 py-3 text-gray-400 font-mono text-xs whitespace-nowrap">
                                      {tx.id}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                                        isCredit ? "bg-teal-50 text-teal-700" : "bg-red-50 text-red-600"
                                      }`}>
                                        {isCredit ? "Credit" : "Debit"}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-700 max-w-[240px] truncate">
                                      {creditReasonLabel(tx.reason)}
                                    </td>
                                    <td className={`px-4 py-3 text-right font-semibold tabular-nums whitespace-nowrap ${
                                      isCredit ? "text-teal-700" : "text-red-500"
                                    }`}>
                                      {isCredit ? "+" : ""}{tx.amount}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {!walletLoading && !walletError && walletCurrentPage?.data?.length > 0 && (walletHasPrev || walletHasNext) && (
                    <div className="flex items-center justify-between mt-4">
                      <button
                        type="button"
                        onClick={handleWalletPrev}
                        disabled={!walletHasPrev || walletPaginating}
                        className="text-sm font-semibold text-teal-700 hover:text-teal-800 disabled:text-gray-300 disabled:cursor-not-allowed cursor-pointer"
                      >
                        ← Previous
                      </button>
                      <span className="text-xs text-gray-400">Page {walletPageIndex + 1}</span>
                      <button
                        type="button"
                        onClick={handleWalletNext}
                        disabled={!walletHasNext || walletPaginating}
                        className="text-sm font-semibold text-teal-700 hover:text-teal-800 disabled:text-gray-300 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {walletPaginating ? "Loading…" : "Next →"}
                      </button>
                    </div>
                  )}
                </Section>

                {/* Came across with the /credits merge — the only way to spend
                    RoamPoints, so the wallet can't show a balance without it. */}
                <RedeemRewards
                  balance={myProfile?.stats?.credits || 0}
                  onRedeemed={() => {
                    refetchProfile?.();
                    setWalletPages([]);
                    setWalletPageIndex(0);
                  }}
                />
              </>
            ) : activeNav === "faqs" ? (
              <>
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900">
                    Frequently Asked Questions
                  </h2>
                </div>
                <FaqAccordion items={WALLET_FAQS} />
              </>
            ) : activeNav === "devices" ? (
              <div className="text-center py-16">
                <Monitor size={32} className="mx-auto text-gray-300 mb-3" />
                <h2 className="text-lg font-semibold text-gray-900">Logged in devices</h2>
                <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                  Session management isn&apos;t available yet. When it lands, you&apos;ll be able
                  to see every device signed into your account and sign them out.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-4 mb-6">
                  <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900">
                    My Profile
                  </h2>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800 transition-colors cursor-pointer disabled:opacity-60"
                  >
                    {saving && <Loader2 size={14} className="animate-spin" />}
                    Save
                  </button>
                </div>

                {!form.dob && (
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3.5 mb-7">
                    <div className="flex items-start gap-3 min-w-0">
                      <Cake size={18} className="text-amber-500 shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Planning a birthday trip?</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Add your date of birth and enjoy a little surprise from us.
                        </p>
                      </div>
                    </div>
                    <a href="#dob" className="text-sm font-semibold text-teal-700 hover:text-teal-800 shrink-0">
                      Add date of birth
                    </a>
                  </div>
                )}

                {/* A user's badges are awarded automatically and used to be
                    visible only on their public profile, which they had no
                    direct link to — so they were effectively invisible to the
                    person who earned them. This is now the one place to see
                    them. Hidden entirely when there are none: an empty shelf
                    with no way to explain how to fill it is just noise. */}
                {myProfile?.badges?.length > 0 && (
                  <Section title="Your badges">
                    {/* `isVerified` is left at its default so VERIFIED_TRAVELER
                        is shown here. The public profile hides it because that
                        page has its own verified tick next to the name; this
                        page has none, so hiding it would lose the badge. */}
                    <BadgeStrip badges={myProfile.badges} />
                  </Section>
                )}

                <Section title="General information">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                    <Field label="First & middle name" error={fieldErrors.firstName}>
                      <input className={`${inputClass} ${fieldErrors.firstName ? inputErrorClass : ""}`} value={form.firstName} onChange={set("firstName")} />
                    </Field>
                    <Field label="Last name">
                      <input className={inputClass} value={form.lastName} onChange={set("lastName")} />
                    </Field>
                    <Field label="Gender">
                      <select className={inputClass} value={form.gender} onChange={set("gender")}>
                        <option value="">Select gender</option>
                        <option value="female">Female</option>
                        <option value="male">Male</option>
                        <option value="other">Other</option>
                        <option value="undisclosed">Prefer not to say</option>
                      </select>
                    </Field>
                    <Field label="Date of birth">
                      <input id="dob" type="date" className={inputClass} value={form.dob} onChange={set("dob")} />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Nationality">
                        <select className={inputClass} value={form.nationality} onChange={set("nationality")}>
                          <option value="">Select nationality</option>
                          <option value="IN">Indian</option>
                          <option value="other">Other</option>
                        </select>
                      </Field>
                    </div>
                  </div>
                </Section>

                <Section
                  title="Contact details"
                  description="Add contact information to receive booking details and other alerts."
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                    <Field label="Mobile number" error={fieldErrors.phone}>
                      <div className="relative">
                        <input
                          className={`${inputClass} pr-10 ${fieldErrors.phone ? inputErrorClass : ""}`}
                          value={form.phone}
                          onChange={set("phone")}
                          placeholder="+91"
                        />
                        {form.phone && (
                          <Check size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-teal-600" />
                        )}
                      </div>
                    </Field>
                    <Field label="Email address">
                      <div className="relative">
                        <input
                          className={`${inputClass} pr-10`}
                          defaultValue={user?.email || ""}
                          placeholder="you@example.com"
                        />
                        {user?.email && (
                          <Check size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-teal-600" />
                        )}
                      </div>
                    </Field>
                  </div>
                </Section>

                <Section title="Document details">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
                    <Field label="Passport no.">
                      <input className={inputClass} value={form.passportNo} onChange={set("passportNo")} />
                    </Field>
                    <Field label="Expiry date">
                      <input type="date" className={inputClass} value={form.passportExpiry} onChange={set("passportExpiry")} />
                    </Field>
                    <Field label="Issuing country">
                      <select className={inputClass} value={form.issuingCountry} onChange={set("issuingCountry")}>
                        <option value="">Select country</option>
                        <option value="IN">India</option>
                        <option value="other">Other</option>
                      </select>
                    </Field>
                    <Field label="PAN card number" error={fieldErrors.panNumber}>
                      <input
                        className={`${inputClass} ${fieldErrors.panNumber ? inputErrorClass : ""}`}
                        value={form.panNumber}
                        onChange={(e) => setForm((prev) => ({ ...prev, panNumber: e.target.value.toUpperCase() }))}
                      />
                    </Field>
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    <span className="font-semibold text-amber-600">Note:</span> your PAN is used only
                    for international bookings, as per RBI guidelines.
                  </p>
                </Section>

                <Section
                  title={
                    <span className="inline-flex items-center gap-2">
                      Auto-add travel insurance / Trip Secure
                      <button
                        type="button"
                        onClick={() => setBenefitsOpen(true)}
                        aria-label="View what Trip Secure covers"
                        title="View what Trip Secure covers"
                        className="text-teal-600 hover:text-teal-700 transition-colors cursor-pointer"
                      >
                        <Info size={16} />
                      </button>
                    </span>
                  }
                  description="Set a default so cover is added automatically to future flight and hotel bookings."
                >
                  <div className="rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                    {INSURANCE_ROWS.map((row) => (
                      <label
                        key={row.key}
                        className="flex items-center gap-4 px-4 py-3.5 cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className="w-48 shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">
                          {row.context}
                        </span>
                        <span className="flex-1 text-sm text-gray-700">{row.label}</span>
                        <input
                          type="checkbox"
                          checked={!!insurance[row.key]}
                          onChange={(e) =>
                            setInsurance((prev) => ({ ...prev, [row.key]: e.target.checked }))
                          }
                          className="w-4 h-4 shrink-0 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                        />
                      </label>
                    ))}
                  </div>
                  <p className="mt-2.5 text-xs text-gray-400 flex items-center gap-1.5">
                    <Info size={12} /> Applies to future bookings only.
                  </p>
                </Section>

                <Section title="Frequent flyer details">
                  {frequentFlyers.length > 0 && (
                    <div className="flex flex-col gap-3 mb-4">
                      {frequentFlyers.map((flyer) => (
                        <div key={flyer.id} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-center">
                          <select
                            className={inputClass}
                            value={flyer.airline}
                            onChange={(e) => updateFrequentFlyer(flyer.id, "airline", e.target.value)}
                            onBlur={() => commitFrequentFlyer(flyer)}
                          >
                            <option value="">Airline</option>
                            {AIRLINES.map((airline) => (
                              <option key={airline} value={airline}>{airline}</option>
                            ))}
                          </select>
                          <input
                            className={inputClass}
                            placeholder="Frequent Flyer Number"
                            value={flyer.number}
                            onChange={(e) => updateFrequentFlyer(flyer.id, "number", e.target.value)}
                            onBlur={() => commitFrequentFlyer(flyer)}
                          />
                          <button
                            type="button"
                            onClick={() => removeFrequentFlyer(flyer.id)}
                            aria-label="Remove frequent flyer programme"
                            className="w-9 h-9 shrink-0 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 flex items-center justify-center transition-colors cursor-pointer justify-self-start"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={addFrequentFlyer}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800 cursor-pointer"
                  >
                    <Plus size={15} /> Add
                  </button>
                </Section>
              </>
            )}
          </div>
        </div>

        <TripSecureBenefitsDialog isOpen={benefitsOpen} onClose={() => setBenefitsOpen(false)} />

        {/* No "View public profile" link: /u/<own handle> now redirects
            straight back here, so it would have been a round trip to itself. */}
      </div>
    </div>
  );
}
